import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { CustomEase } from 'gsap/CustomEase';
import KeenSlider from 'keen-slider';
import 'keen-slider/keen-slider.min.css';

gsap.registerPlugin(ScrollTrigger, CustomEase);

// Example easing registration — uncomment / adjust as needed.
// CustomEase.create('brz-out', 'M0,0 C0.2,1 0.3,1 1,1');
 
/**
 * Primary navigation: dropdown hover (GSAP) + hide-on-scroll-down behavior.
 */
function initSiteHeader() {
  const header = document.querySelector('[data-site-header]');
  if (!header) return;

  // --- Dropdown hover animations (desktop .site-nav only) ------------------
  const desktopNav = header.querySelector('.site-nav');
  const dropdownParents = desktopNav
    ? desktopNav.querySelectorAll('.menu-item-has-children')
    : [];

  dropdownParents.forEach((parent) => {
    const submenu = parent.querySelector(':scope > .sub-menu');
    if (!submenu) return;

    const items = submenu.querySelectorAll(':scope > li');

    // Initial state managed by GSAP so CSS :hover fallback still works.
    gsap.set(submenu, { autoAlpha: 0, y: -8, pointerEvents: 'none' });
    gsap.set(items, { autoAlpha: 0, y: -10 });

    let tl;
    const open = () => {
      if (tl) tl.kill();
      tl = gsap.timeline();
      tl.to(submenu, {
        autoAlpha: 1,
        y: 0,
        duration: 0.35,
        ease: 'power2.out',
        pointerEvents: 'auto',
      }).to(
        items,
        {
          autoAlpha: 1,
          y: 0,
          duration: 0.3,
          ease: 'power2.out',
          stagger: 0.06,
        },
        '-=0.2'
      );
    };

    const close = () => {
      if (tl) tl.kill();
      tl = gsap.timeline();
      tl.to(items, {
        autoAlpha: 0,
        y: -10,
        duration: 0.15,
        ease: 'power2.in',
        stagger: { each: 0.03, from: 'end' },
      }).to(
        submenu,
        {
          autoAlpha: 0,
          y: -8,
          duration: 0.2,
          ease: 'power2.in',
          pointerEvents: 'none',
        },
        '-=0.1'
      );
    };

    parent.addEventListener('mouseenter', open);
    parent.addEventListener('mouseleave', close);
    parent.addEventListener('focusin', open);
    parent.addEventListener('focusout', (e) => {
      if (!parent.contains(e.relatedTarget)) close();
    });
  });

  // --- Hide on scroll down, show on scroll up ------------------------------
  const hideDistance = header.offsetTop + header.offsetHeight + 20;
  let lastY = window.scrollY;
  let hidden = false;
  const threshold = 8; // minimum delta before reacting

  const onScroll = () => {
    const y = window.scrollY;
    const delta = y - lastY;

    if (Math.abs(delta) < threshold) return;

    if (delta > 0 && y > header.offsetTop && !hidden) {
      // Scrolling down: tuck the nav up with the page.
      hidden = true;
      gsap.to(header, {
        y: -hideDistance,
        duration: 0.45,
        ease: 'power1.inOut', // Quad.easeInOut
        overwrite: 'auto',
      });
    } else if (delta < 0 && hidden) {
      // Scrolling up: slide back into place.
      hidden = false;
      gsap.to(header, {
        y: 0,
        duration: 0.45,
        ease: 'power1.inOut', // Quad.easeInOut
        overwrite: 'auto',
      });
    }

    lastY = y;
  };

  window.addEventListener('scroll', onScroll, { passive: true });

  // --- Mobile burger menu --------------------------------------------------
  const burger    = header.querySelector('.site-header__burger');
  const mobileNav = header.querySelector('.site-nav-mobile');
  const mobileBreakpoint = window.matchMedia('(min-width: 768px)');

  if (burger && mobileNav) {
    const lineTop = burger.querySelector('.site-header__burger-line--top');
    const lineMid = burger.querySelector('.site-header__burger-line--mid');
    const lineBot = burger.querySelector('.site-header__burger-line--bot');

    const BAR_HEIGHT = 65;

    // Returns the collapsed clip-path as a fully-resolved px string so GSAP
    // can interpolate without encountering a calc() it cannot parse.
    const collapsedClip = () => {
      const bottomInset = header.offsetHeight - BAR_HEIGHT;
      return `inset(0px 0px ${bottomInset}px 0px)`;
    };

    const expandedClip = () => 'inset(0px 0px 0px 0px)';

    let isOpen    = false;
    let burgerTl  = null;
    let clipTl    = null;

    // Seed GSAP with the resolved px value so its first tween has a valid from.
    if (!mobileBreakpoint.matches) {
      gsap.set(header, { clipPath: collapsedClip() });
    } else {
      gsap.set(header, { clipPath: 'none' });
    }

    const openMenu = () => {
      isOpen = true;
      burger.setAttribute('aria-expanded', 'true');
      mobileNav.setAttribute('aria-hidden', 'false');
      document.body.classList.add('mobile-nav-open');

      if (burgerTl) burgerTl.kill();
      burgerTl = gsap.timeline({ defaults: { duration: 0.8, ease: 'power2.inOut' } });
      burgerTl
        .to(lineMid, { scaleX: 0 }, 0)
        .to(lineTop, { y: 14, rotation: 45 }, 0)
        .to(lineBot, { y: -14, rotation: -45 }, 0);

      if (clipTl) clipTl.kill();
      // Re-read from the current GSAP value so interrupted closes don't jump.
      gsap.set(header, { clipPath: gsap.getProperty(header, 'clipPath') || collapsedClip() });
      clipTl = gsap.to(header, {
        clipPath: expandedClip(),
        duration: 0.8,
        ease: 'power2.inOut',
      });
    };

    const closeMenu = () => {
      isOpen = false;
      burger.setAttribute('aria-expanded', 'false');
      mobileNav.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('mobile-nav-open');

      if (burgerTl) burgerTl.kill();
      burgerTl = gsap.timeline({ defaults: { duration: 0.8, ease: 'power2.inOut' } });
      burgerTl
        .to(lineMid, { scaleX: 1 }, 0)
        .to(lineTop, { y: 0, rotation: 0 }, 0)
        .to(lineBot, { y: 0, rotation: 0 }, 0);

      if (clipTl) clipTl.kill();
      clipTl = gsap.to(header, {
        clipPath: collapsedClip(),
        duration: 0.8,
        ease: 'power2.inOut',
      });
    };

    burger.addEventListener('click', () => {
      if (isOpen) {
        closeMenu();
      } else {
        openMenu();
      }
    });

    // Close if viewport grows to desktop size while menu is open.
    mobileBreakpoint.addEventListener('change', (e) => {
      if (e.matches) {
        if (isOpen) closeMenu();
        gsap.set(header, { clipPath: 'none' });
      } else {
        gsap.set(header, { clipPath: collapsedClip() });
      }
    });
  }
}

/**
 * Hero collage parallax: each image drifts by its own random amount as the
 * page scrolls past the hero, giving the impression of separate depth planes.
 */
function initHeroCollageParallax() {
  const images = document.querySelectorAll('.hero-main__collage-image[data-parallax]');
  if (!images.length) return;

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReducedMotion) return;

  images.forEach((img) => {
    const amount = parseFloat(img.dataset.parallax) || 0;
    const rotation = parseFloat(img.dataset.parallaxRotation) || 0;
    if (!amount) return;

    // Seed GSAP's transform cache with any existing rotation so the parallax
    // translate does not clobber the angled placement set via inline style.
    gsap.set(img, { rotation });

    const trigger = img.closest('.hero-main') || img.closest('.hero-main__images') || img;

    gsap.fromTo(
      img,
      { y: -amount },
      {
        y: amount,
        ease: 'none',
        scrollTrigger: {
          trigger,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true,
        },
      }
    );
  });
}

/**
 * Feature Card Grid block.
 *
 * Behaviors:
 * - Keen slider on mobile only when data-feature-card-slider-mobile-only is set
 * - Prev/next buttons for navigation
 * - Simple slide configuration with perView: 1 for mobile cards
 */
function initFeatureCardGrids() {
  const sliders = document.querySelectorAll('[data-feature-card-slider]');
  if (!sliders.length) return;

  const mdQuery = window.matchMedia('(min-width: 768px)');

  sliders.forEach((el) => {
    const mobileOnly = 'featureCardSliderMobileOnly' in el.dataset;

    const shouldBeActive = () => {
      if (mobileOnly) return !mdQuery.matches;
      return true;
    };

    let keenInstance = null;
    let keydownHandler = null;

    const createSlider = () => {
      if (keenInstance) return;

      keenInstance = new KeenSlider(el, {
        loop: true,
        mode: 'snap',
        slides: {
          perView: 1,
          spacing: 16,
          origin: 'center',
        },
      });

      // Arrow buttons
      const stage = el.closest('.feature-card-grid__stage') || el.parentElement;
      if (stage) {
        const prev = stage.querySelector('[data-feature-card-slider-prev]');
        const next = stage.querySelector('[data-feature-card-slider-next]');
        prev?.addEventListener('click', () => keenInstance.prev());
        next?.addEventListener('click', () => keenInstance.next());
      }

      // Arrow key navigation
      keydownHandler = (e) => {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        const active = document.activeElement;
        const hasFocus = active && (active === el || el.contains(active));
        if (!hasFocus) {
          const rect = el.getBoundingClientRect();
          const inView = rect.top < window.innerHeight && rect.bottom > 0;
          if (!inView) return;
        }
        if (e.key === 'ArrowLeft') keenInstance.prev();
        if (e.key === 'ArrowRight') keenInstance.next();
      };
      document.addEventListener('keydown', keydownHandler);
    };

    const destroySlider = () => {
      if (!keenInstance) return;
      keenInstance.destroy();
      keenInstance = null;
      if (keydownHandler) {
        document.removeEventListener('keydown', keydownHandler);
        keydownHandler = null;
      }
    };

    // Initial mount
    if (shouldBeActive()) createSlider();

    // Respond to viewport crossing the md breakpoint
    if (mobileOnly) {
      mdQuery.addEventListener('change', () => {
        if (shouldBeActive()) {
          createSlider();
        } else {
          destroySlider();
        }
      });
    }
  });
}

/**
 * Card Slider block.
 *
 * Behaviors:
 * - keen-slider, center-origin, perView 'auto' so fixed-width cards show the
 *   focused card centered with partial cards on each side.
 * - Prev/next buttons + arrow key navigation when the slider is focused or
 *   intersecting the viewport.
 * - Per-slide scale written to `--card-scale` each frame from the slider's
 *   own `detailsChanged` loop, so the scaling interpolates in lock-step with
 *   the horizontal motion (same duration + easing as keen-slider itself).
 * - Vertical scroll parallax on card images, toggleable via
 *   `data-scroll-parallax="false"`.
 * - Viewport-conditional init/destroy: when `data-card-slider-desktop-only` is
 *   set the slider is only active at ≥768 px; `data-card-slider-mobile-only`
 *   limits it to <768 px. A matchMedia listener tears down and re-creates the
 *   instance when the viewport crosses that boundary.
 */
function initCardSliders() {
  const sliders = document.querySelectorAll('[data-card-slider]');
  if (!sliders.length) return;

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const mdQuery = window.matchMedia('(min-width: 768px)');

  sliders.forEach((el) => {
    const imageScale = parseFloat(el.dataset.imageScale) || 110; // percent
    const parallaxPct = Math.max(0, (imageScale - 100) / 2); // max ± offset % (Y only now)
    const sideScale = parseFloat(el.dataset.sideScale) || 0.95;
    const transitionDuration = parseInt(el.dataset.transitionDuration, 10) || 1000;
    const enableScrollParallax = el.dataset.scrollParallax !== 'false' && !prefersReducedMotion;
    const desktopOnly = 'cardSliderDesktopOnly' in el.dataset;
    const mobileOnly  = 'cardSliderMobileOnly'  in el.dataset;

    // Quad.easeInOut (Robert Penner) — drives keen-slider's snap animation.
    // Because we sample scales inside detailsChanged, the scale animation
    // inherits this curve automatically.
    const quadEaseInOut = (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t);

    // --- Scaling sync with keen-slider's animator -------------------------
    // Strategy: capture each slide's current scale when `animationStarted`
    // fires, then on every `detailsChanged` (which keen-slider fires every
    // animation frame) interpolate toward the new target scale using the
    // same duration + easing that drives the slider's horizontal motion.
    // The result is a single frame-synced animation — the cards scale in
    // perfect lock-step with the track, not slightly before or after.
    //
    // See https://keen-slider.io/docs — `animationStarted` / `animationEnded`
    // events and the `slider.animator` object.
    const getInner = (slideEl) =>
      slideEl.querySelector('.card-slider__card-inner') || slideEl;

    const readCurrentScale = (slideEl) => {
      const inner = getInner(slideEl);
      const raw = inner.style.getPropertyValue('--card-scale');
      const parsed = parseFloat(raw);
      return Number.isFinite(parsed) ? parsed : sideScale;
    };

    const writeScale = (slideEl, value) => {
      getInner(slideEl).style.setProperty('--card-scale', value.toFixed(4));
    };

    const snapToRestState = (s) => {
      const activeIdx = s.track.details.rel;
      s.slides.forEach((slideEl, i) => {
        writeScale(slideEl, i === activeIdx ? 1 : sideScale);
      });
    };

    // Determines whether the slider should be active for the current viewport.
    const shouldBeActive = () => {
      if (desktopOnly) return mdQuery.matches;
      if (mobileOnly)  return !mdQuery.matches;
      return true;
    };

    // Parallax triggers are stored so they can be cleaned up on destroy.
    let parallaxTriggers = [];

    let keenInstance = null;
    let keydownHandler = null;

    const createSlider = () => {
      if (keenInstance) return; // already live

      // Per-instance animation state, owned by the slider's animator lifecycle.
      let animStart = 0;
      let animFrom = [];
      let animTargetIdx = -1;
      let animating = false;

      keenInstance = new KeenSlider(
        el,
        {
          loop: true,
          mode: 'snap',
          defaultAnimation: {
            duration: transitionDuration,
            easing: quadEaseInOut,
          },
          slides: {
            perView: 'auto',
            spacing: 20,
            origin: 'center',
          },
          created(s) {
            snapToRestState(s);
          },
          // Fired by keen-slider's animator at the very start of a snap
          // animation. Capture current (possibly mid-flight) scales so a
          // rapid second nav press interpolates smoothly from wherever we
          // are — no snapping, no stacked tweens.
          animationStarted(s) {
            animStart = performance.now();
            const animatorTargetAbs =
              typeof s.animator?.targetIdx === 'number' ? s.animator.targetIdx : null;
            animTargetIdx =
              animatorTargetAbs !== null ? s.track.absToRel(animatorTargetAbs) : s.track.details.rel;
            animFrom = s.slides.map(readCurrentScale);
            animating = true;
          },
          // keen-slider fires detailsChanged on every animation frame while
          // the track is moving, so piggy-backing here gives us frame-perfect
          // sync with the horizontal motion (same rAF cadence, same clock).
          detailsChanged(s) {
            if (!animating) return;
            const raw = Math.min((performance.now() - animStart) / transitionDuration, 1);
            const eased = quadEaseInOut(raw);
            s.slides.forEach((slideEl, i) => {
              const target = i === animTargetIdx ? 1 : sideScale;
              const from = animFrom[i] ?? sideScale;
              writeScale(slideEl, from + (target - from) * eased);
            });
            if (raw >= 1) animating = false;
          },
          animationEnded(s) {
            animating = false;
            snapToRestState(s);
          },
        }
      );

      // Arrow buttons (rendered in the PHP template as siblings of the track).
      const stage = el.closest('.card-slider__stage') || el.parentElement;
      if (stage) {
        const prev = stage.querySelector('[data-card-slider-prev]');
        const next = stage.querySelector('[data-card-slider-next]');
        prev?.addEventListener('click', () => keenInstance.prev());
        next?.addEventListener('click', () => keenInstance.next());
      }

      // Arrow key navigation: active when the slider (or a descendant) has
      // focus, OR when the slider is the most-visible card slider on screen.
      keydownHandler = (e) => {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        const active = document.activeElement;
        const hasFocus = active && (active === el || el.contains(active));
        if (!hasFocus) {
          const rect = el.getBoundingClientRect();
          const inView = rect.top < window.innerHeight && rect.bottom > 0;
          if (!inView) return;
        }
        if (e.key === 'ArrowLeft') keenInstance.prev();
        if (e.key === 'ArrowRight') keenInstance.next();
      };
      document.addEventListener('keydown', keydownHandler);

      // Vertical scroll parallax per card image.
      if (enableScrollParallax && parallaxPct) {
        el.querySelectorAll('[data-card-img]').forEach((img) => {
          const card = img.closest('.card-slider__card') || img;
          const trigger = ScrollTrigger.create({
            trigger: card,
            start: 'top bottom',
            end: 'bottom top',
            scrub: true,
            onUpdate: (self) => {
              const y = (self.progress - 0.5) * 2 * parallaxPct;
              img.style.setProperty('--card-img-y', `${y}%`);
            },
          });
          parallaxTriggers.push(trigger);
        });
      }
    };

    const destroySlider = () => {
      if (!keenInstance) return;
      keenInstance.destroy();
      keenInstance = null;
      if (keydownHandler) {
        document.removeEventListener('keydown', keydownHandler);
        keydownHandler = null;
      }
      parallaxTriggers.forEach((t) => t.kill());
      parallaxTriggers = [];
      // Reset any lingering inline scale vars on slide inner elements.
      el.querySelectorAll('.card-slider__card-inner').forEach((inner) => {
        inner.style.removeProperty('--card-scale');
      });
    };

    // Initial mount.
    if (shouldBeActive()) createSlider();

    // Respond to viewport crossing the md breakpoint.
    if (desktopOnly || mobileOnly) {
      mdQuery.addEventListener('change', () => {
        if (shouldBeActive()) {
          createSlider();
        } else {
          destroySlider();
        }
      });
    }
  });
}

/**
 * Accolade block.
 *
 * Behaviors:
 * - keen-slider with `loop: true`, `perView: 1`, Quad.easeInOut snap.
 * - Cross-fade background layers (one per accolade) by toggling `is-active`.
 * - Per-active-slide stats fade in 1s with 0.1s stagger (CSS transitions
 *   driven by --stat-index + .is-visible).
 * - Optional autoplay (ACF `auto_play` + `duration` seconds), paused on
 *   pointer/focus and respecting prefers-reduced-motion.
 * - Single-accolade mode: skips keen-slider, just animates stats once on view.
 */
function initAccoladeSliders() {
  const blocks = document.querySelectorAll('.accolade');
  if (!blocks.length) return;

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const quadEaseInOut = (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t);

  const showStats = (slideEl) => {
    if (!slideEl) return;
    const stats = slideEl.querySelectorAll('[data-accolade-stat]');
    if (!stats.length) return;
    // Reset, then re-add on next frame so the staggered transition replays.
    stats.forEach((s) => s.classList.remove('is-visible'));
    if (prefersReducedMotion) {
      stats.forEach((s) => s.classList.add('is-visible'));
      return;
    }
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        stats.forEach((s) => s.classList.add('is-visible'));
      });
    });
  };

  blocks.forEach((block) => {
    const track = block.querySelector('[data-accolade-slider]');
    const bgs = block.querySelectorAll('[data-accolade-bg]');
    const slides = block.querySelectorAll('[data-accolade-slide]');

    // Single-accolade (no slider) — animate stats once when in view.
    if (!track) {
      const single = block.querySelector('[data-accolade-slide]');
      if (!single) return;
      const io = new IntersectionObserver((entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            showStats(single);
            obs.disconnect();
          }
        });
      }, { threshold: 0.25 });
      io.observe(single);
      return;
    }

    const transitionDuration = parseInt(track.dataset.transitionDuration, 10) || 900;
    const autoplayEnabled =
      track.dataset.autoplay === 'true' && !prefersReducedMotion;
    const autoplayDelay = parseInt(track.dataset.duration, 10) || 3000;

    const setActiveBg = (idx) => {
      bgs.forEach((bg) => {
        if (bg.classList.contains('accolade__bg--base')) return;
        const bgIdx = parseInt(bg.dataset.index, 10);
        bg.classList.toggle('is-active', bgIdx === idx);
      });
    };

    const setActiveDot = (idx) => {
      const dots = block.querySelectorAll('[data-accolade-dot]');
      dots.forEach((d) => {
        const dIdx = parseInt(d.dataset.index, 10);
        d.classList.toggle('is-active', dIdx === idx);
      });
    };

    const slider = new KeenSlider(track, {
      loop: true,
      mode: 'snap',
      defaultAnimation: {
        duration: transitionDuration,
        easing: quadEaseInOut,
      },
      slides: { perView: 1, spacing: 0, origin: 'auto' },
      created(s) {
        const idx = s.track.details.rel;
        setActiveBg(idx);
        setActiveDot(idx);
        showStats(slides[idx]);
      },
      slideChanged(s) {
        const idx = s.track.details.rel;
        setActiveBg(idx);
        setActiveDot(idx);
        // Hide stats on all non-active slides; replay on the active one.
        slides.forEach((slideEl, i) => {
          if (i !== idx) {
            slideEl
              .querySelectorAll('[data-accolade-stat]')
              .forEach((st) => st.classList.remove('is-visible'));
          }
        });
        showStats(slides[idx]);
      },
    });

    // Prev / next buttons.
    const stage = track.closest('.accolade__stage') || block;
    const prev = stage.querySelector('[data-accolade-prev]');
    const next = stage.querySelector('[data-accolade-next]');
    prev?.addEventListener('click', () => slider.prev());
    next?.addEventListener('click', () => slider.next());

    // Dots.
    block.querySelectorAll('[data-accolade-dot]').forEach((dot) => {
      dot.addEventListener('click', () => {
        const i = parseInt(dot.dataset.index, 10);
        if (Number.isFinite(i)) slider.moveToIdx(i);
      });
    });

    // Keyboard nav (focus or in-view).
    const onKeydown = (e) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      const active = document.activeElement;
      const hasFocus = active && (active === track || track.contains(active));
      if (!hasFocus) {
        const rect = block.getBoundingClientRect();
        const inView = rect.top < window.innerHeight && rect.bottom > 0;
        if (!inView) return;
      }
      if (e.key === 'ArrowLeft') slider.prev();
      if (e.key === 'ArrowRight') slider.next();
    };
    document.addEventListener('keydown', onKeydown);

    // Autoplay with hover/focus pause.
    if (autoplayEnabled) {
      let timer = null;
      const start = () => {
        stop();
        timer = setInterval(() => slider.next(), autoplayDelay);
      };
      const stop = () => {
        if (timer) {
          clearInterval(timer);
          timer = null;
        }
      };
      block.addEventListener('pointerenter', stop);
      block.addEventListener('pointerleave', start);
      block.addEventListener('focusin', stop);
      block.addEventListener('focusout', (e) => {
        if (!block.contains(e.relatedTarget)) start();
      });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) stop(); else start();
      });
      start();
    }
  });
}

/**
 * FAQ Accordion block.
 *
 * Behaviors:
 * - Intercepts <summary> clicks to prevent the native <details> toggle so
 *   GSAP fully controls the open/close animation.
 * - Animates answer panel height (0 → scrollHeight) and chevron rotation
 *   (0° → 180°) via GSAP.
 * - `data-faq-only-one`: closing any open sibling before opening a new one.
 * - `data-faq-start`: pre-opens first/all items on load without animation.
 * - Non-collapsible sections (no [data-faq-collapsible]): answers already
 *   rendered open server-side; no JS needed.
 */
function initFaqAccordion() {
  const sections = document.querySelectorAll('[data-faq-collapsible]');
  if (!sections.length) return;

  const DURATION  = 0.4;
  const EASE_OPEN  = 'power2.out';
  const EASE_CLOSE = 'power2.inOut';

  const openItem = (detail, animate = true) => {
    const answer  = detail.querySelector('.faq-accordion__answer');
    const chevron = detail.querySelector('.faq-accordion__chevron');
    if (!answer) return;

    detail.open = true;
    const targetH = answer.scrollHeight;

    if (animate) {
      gsap.to(answer, {
        height: targetH,
        duration: DURATION,
        ease: EASE_OPEN,
        onComplete: () => {
          // Allow natural reflow after open (e.g. window resize).
          answer.style.height = 'auto';
        },
      });
      if (chevron) {
        gsap.to(chevron, { rotation: 180, duration: DURATION, ease: EASE_OPEN });
      }
    } else {
      gsap.set(answer, { height: targetH });
      answer.style.height = 'auto';
      if (chevron) gsap.set(chevron, { rotation: 180 });
    }
  };

  const closeItem = (detail, animate = true) => {
    const answer  = detail.querySelector('.faq-accordion__answer');
    const chevron = detail.querySelector('.faq-accordion__chevron');
    if (!answer) return;

    if (animate) {
      // Snap from 'auto' to a px value so GSAP can tween down to 0.
      gsap.set(answer, { height: answer.offsetHeight });
      gsap.to(answer, {
        height: 0,
        duration: DURATION,
        ease: EASE_CLOSE,
        onComplete: () => { detail.open = false; },
      });
      if (chevron) {
        gsap.to(chevron, { rotation: 0, duration: DURATION, ease: EASE_CLOSE });
      }
    } else {
      gsap.set(answer, { height: 0 });
      if (chevron) gsap.set(chevron, { rotation: 0 });
      detail.open = false;
    }
  };

  sections.forEach((section) => {
    const items    = Array.from(section.querySelectorAll('.faq-accordion__item'));
    const onlyOne  = 'faqOnlyOne' in section.dataset;
    const startVal = section.dataset.faqStart || 'none';

    if (!items.length) return;

    // Set initial open state without animation (server-side `open` attr drives
    // which items start open; we just need to size the answer panels).
    items.forEach((detail) => {
      const answer  = detail.querySelector('.faq-accordion__answer');
      const chevron = detail.querySelector('.faq-accordion__chevron');
      if (!answer) return;

      if (detail.open) {
        gsap.set(answer, { height: 'auto' });
        if (chevron) gsap.set(chevron, { rotation: 180 });
      } else {
        gsap.set(answer, { height: 0 });
        if (chevron) gsap.set(chevron, { rotation: 0 });
      }
    });

    // Intercept clicks on every summary.
    items.forEach((detail) => {
      const summary = detail.querySelector('summary');
      if (!summary) return;

      summary.addEventListener('click', (e) => {
        e.preventDefault();

        if (detail.open) {
          closeItem(detail);
        } else {
          if (onlyOne) {
            items.forEach((sibling) => {
              if (sibling !== detail && sibling.open) closeItem(sibling);
            });
          }
          openItem(detail);
        }
      });
    });
  });
}

/**
 * Number Display block.
 *
 * Behaviors:
 * - ScrollTrigger fires once when the block enters ~30% of the viewport.
 * - Adds .is-visible to each item (CSS handles staggered fade/slide-up via --item-index).
 * - GSAP tweens a plain {val} object from 0 to the numeric target, writing the
 *   interpolated value (+ preserved prefix/suffix) to the .number-display__number span.
 * - Stagger achieved via per-item delay: index * 0.08s.
 * - prefers-reduced-motion: skips tweens and renders final values immediately.
 */
function initNumberDisplay() {
  const blocks = document.querySelectorAll('[data-number-display-items]');
  if (!blocks.length) return;

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /**
   * Parse a raw value string into { prefix, numeric, suffix, decimals }.
   * Supports values like "94%", "$2.5M", "1,200+", "3.14", "42".
   * If no numeric portion is found, returns null.
   */
  const parseValue = (raw) => {
    const match = raw.match(/^([^0-9]*)([0-9][0-9,.]*)([^0-9]*)$/);
    if (!match) return null;

    const prefix   = match[1] || '';
    const numStr   = match[2].replace(/,/g, ''); // strip thousands separators
    const suffix   = match[3] || '';
    const numeric  = parseFloat(numStr);
    const decimals = (numStr.includes('.')) ? (numStr.split('.')[1] || '').length : 0;

    if (isNaN(numeric)) return null;

    return { prefix, numeric, suffix, decimals };
  };

  /**
   * Format a number back with thousands commas + original decimal places.
   */
  const formatNumber = (val, decimals) => {
    const fixed = val.toFixed(decimals);
    const parts = fixed.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  };

  blocks.forEach((list) => {
    const items = Array.from(list.querySelectorAll('[data-number-display-item]'));
    if (!items.length) return;

    ScrollTrigger.create({
      trigger: list,
      start: 'top 70%',
      once: true,
      onEnter() {
        items.forEach((item, index) => {
          item.classList.add('is-visible');

          const raw     = item.dataset.value || '';
          const numSpan = item.querySelector('.number-display__number');
          if (!numSpan || !raw) return;

          const parsed = parseValue(raw);
          if (!parsed) return;

          if (prefersReducedMotion) {
            numSpan.textContent = parsed.prefix + formatNumber(parsed.numeric, parsed.decimals) + parsed.suffix;
            return;
          }

          const obj = { val: 0 };
          gsap.to(obj, {
            val: parsed.numeric,
            duration: 1.5,
            delay: index * 0.08,
            ease: 'power2.out',
            onUpdate() {
              numSpan.textContent = parsed.prefix + formatNumber(obj.val, parsed.decimals) + parsed.suffix;
            },
            onComplete() {
              numSpan.textContent = parsed.prefix + formatNumber(parsed.numeric, parsed.decimals) + parsed.suffix;
            },
          });
        });
      },
    });
  });
}

/**
 * Move Gravity Forms validation errors below the form.
 */
function moveGformErrorsBelowForm() {
  const wrappers = document.querySelectorAll('.gform_wrapper');
  wrappers.forEach((wrapper) => {
    const errors = wrapper.querySelector('.gform_validation_errors, [aria-live="polite"]');
    const form = wrapper.querySelector('form');
    if (errors && form) {
      form.after(errors);
    }
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initSiteHeader();
  initHeroCollageParallax();
  initFeatureCardGrids();
  initCardSliders();
  initAccoladeSliders();
  initFaqAccordion();
  initNumberDisplay();
  moveGformErrorsBelowForm();

  // Fade-in any [data-brz-fade] elements on scroll.
  document.querySelectorAll('[data-brz-fade]').forEach((el) => {
    gsap.from(el, {
      opacity: 0,
      y: 24,
      duration: 0.8,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: el,
        start: 'top 85%',
      },
    });
  });

  // Auto-init any [data-brz-slider] element as a keen-slider.
  document.querySelectorAll('[data-brz-slider]').forEach((el) => {
    // eslint-disable-next-line no-new
    new KeenSlider(el, {
      loop: true,
      slides: { perView: 1, origin: "center", spacing: 16 },
      breakpoints: {
        '(min-width: 768px)': { slides: { origin: "center", perView: 2, spacing: 24 } },
        '(min-width: 1024px)': { slides: { origin: "center", perView: 3, spacing: 32 } },
      },
    });
  });
});
